import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Button } from "@/shared/components/ui/button";
import { ApiError } from "@/shared/api/http-client";
import { formatCoordinates } from "@/shared/lib/format-coordinates";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import { FoundingPartnerBadge } from "@/features/founding/FoundingPartnerBadge";
import { FoundingEnrollmentSection } from "@/features/founding/FoundingEnrollmentSection";
import { useFoundingAccount } from "@/features/founding/use-founding-account";
import { usePropertyDetail } from "./use-property-detail";
import { PropertyStatusBadge } from "./PropertyStatusBadge";
import { EditPropertyDialog } from "./EditPropertyDialog";
import { formatDateShort, formatListingPlatform, formatPropertyLocation } from "./format-property";
import type { PropertyDetail } from "./types";

const EDIT_ROLES: readonly Role[] = [Role.WANDERFIT_ADMIN, Role.WANDERFIT_SUPPORT];

export function PropertyDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: property, isLoading, isError, error } = usePropertyDetail(id);
  const role = useCurrentUser()?.role;
  const canEdit = role != null && EDIT_ROLES.includes(role);
  const [editOpen, setEditOpen] = useState(false);

  // Owner's account-level Founding Partner badge (ADR-0010 §9). The
  // founding-account read is ADMIN + FINANCE on the API and FINANCE cannot
  // open this page (`SECTION_ROLES.properties`), so only ADMIN reads it;
  // SUPPORT would 403, so the read never fires for them.
  const canReadFounding = role === Role.WANDERFIT_ADMIN;
  const founding = useFoundingAccount(
    property?.owner.id ?? "",
    Boolean(canReadFounding && property?.owner.id),
  );

  if (isLoading) {
    return (
      <div data-testid="property-detail-loading" className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    const status = error instanceof ApiError ? error.status : undefined;
    return (
      <div data-testid="property-detail-error" className="space-y-4">
        <BackLink label={t("properties.detail.back")} />
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="font-sans text-sm font-medium text-destructive">
            {status === 404 ? t("properties.detail.notFound") : t("properties.detail.error")}
          </p>
        </div>
      </div>
    );
  }

  if (!property) return null;

  return (
    <div data-testid="property-detail-screen" className="space-y-6">
      <BackLink label={t("properties.detail.back")} testId="property-detail-back" />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="brand-eyebrow">{t("properties.detail.eyebrow")}</p>
          <h1 className="text-3xl">{property.name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <PropertyStatusBadge status={property.status} />
            <TestPartitionBadge isTest={property.isTest} testId="property-detail-test-badge" />
            {canReadFounding && (
              <FoundingPartnerBadge
                active={founding.data?.foundingPartner ?? false}
                testId="property-detail-founding-badge"
              />
            )}
          </div>
        </div>
        {canEdit && (
          <Button
            data-testid="edit-property-button"
            variant="outline"
            onClick={() => setEditOpen(true)}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
            {t("properties.detail.edit")}
          </Button>
        )}
      </header>

      <section
        data-testid="property-detail-capacity"
        className="rounded-lg border border-border bg-card p-6"
      >
        <h2 className="brand-eyebrow mb-4">{t("properties.detail.capacity")}</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t("properties.detail.maxGuests")}
            value={t("properties.detail.guests", { count: property.maxGuests })}
            testId="property-maxguests"
          />
          <Field
            label={t("properties.detail.dailyPassCap")}
            value={t("properties.detail.passesPerDay", {
              count: property.dailyPassCap,
            })}
            testId="property-dailypasscap"
          />
          <Field
            label={t("properties.detail.subscriptionTier")}
            value={property.subscriptionTier || "—"}
            testId="property-subscription-tier"
          />
          <Field
            label={t("properties.detail.subscriptionStatus")}
            value={property.subscriptionStatus || "—"}
            testId="property-subscription-status"
          />
        </dl>
      </section>

      <section
        data-testid="property-detail-profile"
        className="rounded-lg border border-border bg-card p-6"
      >
        <h2 className="brand-eyebrow mb-4">{t("properties.detail.details")}</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t("properties.detail.location")}
            value={formatPropertyLocation(property) || "—"}
          />
          <Field label={t("properties.detail.zipCode")} value={property.zipCode || "—"} />
          <Field
            label={t("properties.detail.coordinates")}
            value={formatCoordinates(property.latitude, property.longitude) || "—"}
          />
          <Field
            label={t("properties.detail.geofenceRadius")}
            value={t("properties.detail.miles", { value: property.geofenceRadius })}
          />
          <Field label={t("properties.detail.timezone")} value={property.timezone || "—"} />
          <Field
            label={t("properties.detail.created")}
            value={formatDateShort(property.createdAt, i18n.language) || "—"}
          />
          <Field
            label={t("properties.detail.updated")}
            value={formatDateShort(property.updatedAt, i18n.language) || "—"}
          />
          <Field label={t("properties.detail.id")} value={property.id} mono />
          <div className="sm:col-span-2">
            <Field label={t("properties.detail.description")} value={property.description || "—"} />
          </div>
        </dl>
      </section>

      <ListingsSection listings={property.listings} />

      <section
        data-testid="property-detail-owner"
        className="rounded-lg border border-border bg-card p-6"
      >
        <h2 className="brand-eyebrow mb-4">{t("properties.detail.owner")}</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t("properties.detail.ownerName")}
            value={property.owner.displayName || "—"}
          />
          <Field label={t("properties.detail.ownerEmail")} value={property.owner.email || "—"} />
          <Field label={t("properties.detail.ownerId")} value={property.owner.id} mono />
        </dl>
      </section>

      {canReadFounding && <FoundingEnrollmentSection propertyId={property.id} />}

      {canEdit && (
        <EditPropertyDialog
          property={property}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSuccess={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}

function ListingsSection({ listings }: { readonly listings: PropertyDetail["listings"] }) {
  const { t } = useTranslation();
  // Defensive: the type says non-null, but guard against API drift returning
  // null for a property with no listings (consistent with the `|| "—"` pattern).
  const items = listings ?? [];
  return (
    <section
      data-testid="property-detail-listings"
      className="rounded-lg border border-border bg-card p-6"
    >
      <h2 className="brand-eyebrow mb-4">{t("properties.detail.listings")}</h2>
      {items.length === 0 ? (
        <p
          data-testid="property-listings-empty"
          className="font-sans text-sm text-muted-foreground"
        >
          {t("properties.detail.listingsEmpty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((listing) => (
            <li
              key={listing.id}
              data-testid={`property-listing-${listing.id}`}
              className="flex flex-wrap items-center gap-2"
            >
              <span className="inline-flex items-center rounded-full bg-secondary/10 px-2 py-0.5 font-sans text-xs font-medium text-secondary-foreground">
                {formatListingPlatform(listing.platform, t)}
              </span>
              <span className="break-all font-mono text-xs text-foreground">
                {listing.externalListingId}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BackLink({ label, testId }: { readonly label: string; readonly testId?: string }) {
  return (
    <Link
      to="/properties"
      data-testid={testId}
      className="inline-flex items-center gap-1 font-sans text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      {label}
    </Link>
  );
}

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly testId?: string;
}

function Field({ label, value, mono, testId }: FieldProps) {
  return (
    <div>
      <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        data-testid={testId}
        className={
          mono
            ? "mt-1 font-mono text-xs font-normal text-foreground"
            : "mt-1 font-sans text-sm font-normal text-foreground"
        }
      >
        {value}
      </dd>
    </div>
  );
}
